/* -----------------UI--------------- */
import { Button, Dialog } from "react-native-paper";

import { useKeyboard } from "@react-native-community/hooks";
import { useRef, useState } from "react";
/* -----------------Helpers & Hooks--------------- */
import { FEEDBACK_TOAST_DURATION_MS, useToast } from "../Toast";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
/* -----------------Types--------------- */
import type { IconType } from "../../index";
import DialogWrapper from "../DialogWrapper";
import ScrollContainer from "../ScrollContainer";
/* -----------------Child components--------------- */
import { FeedbackContent } from "./FeedbackContent";
import { FeedbackHeader } from "./FeedbackHeader";
import type { FeedbackParams, HandleTracking } from "./types";
export { FeedbackHeader } from "./FeedbackHeader";
export { FeedbackReasons } from "./FeedbackReasons";
export { FeedbackForm } from "./FeedbackForm";
export const events = {
	dialogOpened: "mem_dialog_opened",
	dialogClosed: "mem_dialog_closed",
	feedbackSubmitted: "mem_feedback_submitted",
	feedbackPositive: "mem_feedback_positive",
	feedbackNegative: "mem_feedback_negative",
};
interface Props {
	visible: boolean;
	onDismiss: () => void;
	feedbackDialogParams: FeedbackParams;
	sendFeedback: (params: FeedbackParams, feedback: string) => void;
	handleTracking: HandleTracking;
	title?: string;
	subtitle?: string;
	cta?: string;
	cancelCta?: string;
	icon?: IconType;
	feedbackReasonList?: {
		title: string;
		description: string;
		icon: IconType;
		value: string;
	}[];
	feedbackLimitError?: string;
	successMessage?: string;
	feedbackPlaceholder?: string;
	handlePostFeedback?: (feedback: string, email: string) => void;
}

const FeedbackDialog = ({
	visible,
	onDismiss,
	feedbackDialogParams,
	sendFeedback,
	handleTracking,
	title,
	subtitle,
	feedbackPlaceholder,
	cta,
	icon = "comment-quote-outline",
	feedbackReasonList,
	cancelCta,
	successMessage,
	feedbackLimitError,
	handlePostFeedback,
}: Props) => {
	const { t } = useTranslation();
	const { showToast } = useToast();
	const { keyboardHeight } = useKeyboard();
	const feedbackContentRef = useRef<{ submit: () => void }>(null);
	const [sendingFeedback, setSendingFeedback] = useState(false);
	const [isValid, setIsValid] = useState(false);

	const finalTitle = title ?? t("dialog.feedback.title");
	const finalSubtitle = subtitle ?? t("dialog.feedback.body");
	const finalCta = cta ?? t("dialog.feedback.cta");
	const finalCancelCta = cancelCta ?? t("common.cancel");
	const finalSuccessMessage = successMessage ?? t("feedback.successMessage");
	const finalPlaceholder =
		feedbackPlaceholder ?? t("dialog.feedback.placeholder");
	const finalLimitError =
		feedbackLimitError ??
		t("report.sessionAnalysis.feedbackDialog.feedbackLimitError");

	useEffect(() => {
		if (visible) {
			handleTracking(events.dialogOpened);
		}
	}, [handleTracking, visible]);

	const handleCloseDialog = () => {
		handleTracking(events.dialogClosed);
		onDismiss();
	};

	return (
		<>
			<DialogWrapper
				visible={visible}
				width={"35%"}
				showCloseButton={false}
				handleClose={handleCloseDialog}
				bottom={keyboardHeight ? keyboardHeight - 200 : 0}
			>
				<FeedbackHeader
					icon={icon}
					title={finalTitle}
					subtitle={finalSubtitle}
				/>
				<ScrollContainer>
					<Dialog.Content
						style={{
							gap: 16,
							padding: 0,
						}}
					>
						<FeedbackContent
							ref={feedbackContentRef}
							sendFeedback={sendFeedback}
							feedbackDialogParams={feedbackDialogParams}
							onDismiss={handleCloseDialog}
							handlePostFeedback={handlePostFeedback}
							subtitle={finalSubtitle}
							feedbackPlaceholder={finalPlaceholder}
							feedbackLimitError={finalLimitError}
							feedbackReasonList={feedbackReasonList}
							showFeedbackConfirmationToast={() =>
								showToast({
									message: finalSuccessMessage,
									duration: FEEDBACK_TOAST_DURATION_MS,
								})
							}
							onStateChange={(sending, valid) => {
								setSendingFeedback(sending);
								setIsValid(valid);
							}}
						/>
					</Dialog.Content>
				</ScrollContainer>
				<Dialog.Actions style={{ alignItems: "center", padding: 24 }}>
					<Button onPress={handleCloseDialog}>{finalCancelCta}</Button>
					<Button
						onPress={() => feedbackContentRef.current?.submit()}
						loading={sendingFeedback}
						disabled={!isValid}
						mode="contained"
						style={{ minWidth: 140 }}
					>
						{finalCta}
					</Button>
				</Dialog.Actions>
			</DialogWrapper>
		</>
	);
};

export { FeedbackDialog };
